import { Chip } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, TableScroller } from '../../components/common';
import { formatRelative } from '../../lib/format';
import { joinParts, Kpi } from './shared';
import { groupCallers, type AuthUsageService, type CallerGroup, type useAuthUsage } from './authUsage';

type Usage = ReturnType<typeof useAuthUsage>;

const STATE: Record<CallerGroup['state'], { label: string; tone: string }> = {
  legacy: { label: 'legacy', tone: 'nyk-tone-amber' },
  migrating: { label: 'migrating', tone: 'nyk-tone-amber' },
  auth: { label: 'on AUTH', tone: 'nyk-tone-green' },
};

const n = (v: number) => v.toLocaleString();

function GroupRow({ g }: { g: CallerGroup }) {
  const title = g.name ? joinParts([g.name.customer, g.name.tenant]) : null;
  return (
    <tr data-group={g.key} data-state={g.state}>
      <td className="nyk-card-title">
        {title && <b data-testid="caller-name">{title}</b>}
        <div className="nyk-inline">
          {g.callers.map((c) => (
            <span key={`${c.method}|${c.callerKey}`} data-caller={c.callerKey} data-method={c.method}>
              <code>{c.callerKey}</code>{' '}
              <span className="nyk-ui-hint">
                {joinParts([
                  c.method === 'jwt' ? `jwt${c.kind && c.kind !== 'legacy' ? ` (${c.kind})` : ''}` : c.method,
                  n(c.count),
                  c.rejected > 0 && `${n(c.rejected)} rejected`,
                ])}
              </span>
            </span>
          ))}
        </div>
      </td>
      <td data-label="State">
        <Chip size="sm" className={STATE[g.state].tone}>
          {STATE[g.state].label}
        </Chip>
      </td>
      <td data-label="Legacy">{n(g.legacy)}</td>
      <td data-label="JWT">{n(g.jwt)}</td>
      <td
        data-label="Rejected"
        title="Answered 401/403: a rejected legacy caller is already blocked by its mode"
      >
        {g.rejected ? n(g.rejected) : '—'}
      </td>
      <td data-label="Last seen">{formatRelative(g.lastSeen)}</td>
    </tr>
  );
}

function ServiceCard({ usage, s }: { usage: Usage; s: AuthUsageService }) {
  const groups = groupCallers(s, usage.nameOf);
  const legacy = groups.filter((g) => g.state !== 'auth');
  return (
    <div className="nyk-card" data-service={`${s.service}:${s.target ?? ''}`}>
      <div className="nyk-card-head">
        <h2>
          {s.label}
          <span className="nyk-ui-hint">
            {' '}
            {joinParts([s.target, s.pods && `${s.pods.answered}/${s.pods.total} pods`])}
          </span>
        </h2>
        {!s.error && (
          <Chip size="sm" className={legacy.length ? 'nyk-tone-amber' : 'nyk-tone-green'}>
            {legacy.length ? `${legacy.length} still on legacy` : 'all on AUTH'}
          </Chip>
        )}
      </div>
      <ErrorBox error={s.error && `Cannot read auth usage: ${s.error}`} />
      {s.warning && <div className="nyk-notice nyk-notice-warn">{s.warning}</div>}
      {!s.error && (
        <>
          <div className="nyk-kpis">
            <Kpi label="Callers on legacy" value={legacy.length} tone={legacy.length ? 'amber' : undefined} />
            <Kpi
              label="Callers on AUTH"
              value={groups.length - legacy.length}
              tone={groups.length > legacy.length ? 'green' : undefined}
            />
          </div>
          {groups.length > 0 ? (
            <TableScroller>
              <table className="nyk-ui-table nyk-cards">
                <thead>
                  <tr>
                    <th>Caller</th>
                    <th>State</th>
                    <th>Legacy</th>
                    <th>JWT</th>
                    <th>Rejected</th>
                    <th>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <GroupRow key={g.key} g={g} />
                  ))}
                </tbody>
              </table>
            </TableScroller>
          ) : (
            <p className="nyk-ui-hint">No calls recorded since the pods started.</p>
          )}
        </>
      )}
    </div>
  );
}

/** F3: per service, the callers still on their legacy credential vs those already sending AUTH tokens. */
export function MigrationSection({ usage }: { usage: Usage }) {
  if (!usage.enabled) return null;
  const list = usage.data ?? [];
  return (
    <section
      className="oph-dash-section"
      id="migration"
      aria-label="Migration to AUTH"
      data-testid="migration"
    >
      <div className="oph-dash-section-head">
        <h2>
          Migration to AUTH{' '}
          <span className="nyk-ui-hint">callers per service and auth method, since the pods started</span>
        </h2>
      </div>
      <ErrorBox error={usage.error} />
      {usage.data && !list.length && (
        <EmptyState
          card
          title="No services report auth usage yet"
          hint="Services report it once they use the AUTH consumer library; add them under AuthUsage:Sources."
        />
      )}
      {list.map((s) => (
        <ServiceCard key={`${s.service}:${s.target ?? ''}`} usage={usage} s={s} />
      ))}
    </section>
  );
}
